'use client';

import type { HeadingBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import type { ElementType } from 'react';
import { HeadingMagazine } from './heading-variants/Magazine';
import { HeadingOversized } from './heading-variants/Oversized';
import { HeadingEyebrowStacked } from './heading-variants/EyebrowStacked';

interface HeadingBlockRendererProps {
  block: HeadingBlock;
  editing?: boolean;
}

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4';

const headingComponents: Record<HeadingLevel, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
};

const headingSizes: Record<HeadingLevel, string> = {
  h1: 'text-4xl md:text-5xl font-bold',
  h2: 'text-3xl md:text-4xl font-bold',
  h3: 'text-2xl md:text-3xl font-semibold',
  h4: 'text-xl md:text-2xl font-semibold',
};

/**
 * Renders a heading with optional subtitle
 */
export function HeadingBlockRenderer({ block, editing }: HeadingBlockRendererProps) {
  switch (block.data.variant ?? 'default') {
    case 'magazine':
      return <HeadingMagazine block={block} editing={editing} />;
    case 'oversized':
      return <HeadingOversized block={block} editing={editing} />;
    case 'eyebrow-stacked':
      return <HeadingEyebrowStacked block={block} editing={editing} />;
    default:
      break;
  }

  const { text, level, subtitle, alignment = 'left' } = block.data;

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const HeadingTag = headingComponents[level as HeadingLevel];

  return (
    <div className={cn('my-8', alignmentClasses[alignment])}>
      <HeadingTag
        className={cn(headingSizes[level as HeadingLevel], 'text-foreground')}
        {...(editing ? { 'data-editable': 'data.text' } : {})}
      >
        {text}
      </HeadingTag>
      {subtitle && (
        <p
          className="mt-3 text-lg text-muted-foreground max-w-2xl"
          {...(editing ? { 'data-editable': 'data.subtitle' } : {})}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}
